/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import DropdownMenu from 'dropdown-menu'
import LandingPage from '../LandingPage'
import { getValue } from 'remote-config-value'
import { getFeatureEnabled } from 'feature-flag'
import { makeStyles } from '@material-ui/core/styles'
import ListContainer from '../../shared/ListContainer'
import DropdownButton from '../../shared/DropdownButton'
import { Alert, Table } from 'foundations-library/components'
import ConfirmDeleteModal from '../../shared/ConfirmDeleteModal'
import { getDeleteSmsNumberParams } from '../../../utils/payload-generator'
import { SmsNumberCampaignContext } from '../../../contexts/SmsNumberCampaignContext'

const styles = makeStyles(() => {
    return {
        deleteMenuItem: {
            color: '#F5472C !important'
        },
        listErrorAlert: {
            marginBottom: '20px'
        }
    }
})

type MenuItem = {
    text: string
    onClick: () => void
    disabled?: boolean
    tooltipText?: string | null
    color?: string
    className?: string
    'data-testid': string
}

interface Props {
    onClickAddNew(): unknown
}
/**
 *
 */
const List = ({
    onClickAddNew
}: Props) => {
    const [deleteSmsNumberFeature, setDeleteSmsNumberFeature] = useState(false)

    const classes = styles()

    const {
        networkPending,
        campaignsWithNumber,
        deleteNumber,
        numberNetworkError,
        setNumberNetworkError
    } = useContext(SmsNumberCampaignContext)

    useEffect(() => {
        getFeatureEnabled('ten_dlc_delete_campaign_number_enabled').then(setDeleteSmsNumberFeature)
    }, [])

    const [deleting, setDeleting] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const deleteReachedNetworkPending = useRef(false)

    useEffect(() => {
        setNumberNetworkError(null)
    }, [])

    const getDeleteNumberItem = (row): MenuItem | null => {
        if (!deleteSmsNumberFeature) return null

        return {
            text: 'Delete',
            onClick: () => setDeleting(row),
            'data-testid': `item-delete-${row.voip_did_id}`
        }
    }

    const renderOptionsColumn = (row) => {
        const menuItems: MenuItem[] = []

        const testUniqueIdentifier = row.csp_campaign_id || row.csp_brand_id || row.voip_did_id

        const deleteMenuItem = getDeleteNumberItem(row)

        if (deleteMenuItem) {
            menuItems.push({
                ...deleteMenuItem,
                text: 'Delete',
                className: classes.deleteMenuItem,
                'data-testid': `item-delete-${testUniqueIdentifier}`
            })
        }

        return menuItems.length > 0
            ? (
                <DropdownMenu
                    button={DropdownButton}
                    menuItems={menuItems}
                    dropdownStyle='configure'
                    data-testid={`item-drop-down-${testUniqueIdentifier}`}
                />
            )
            : <></>
    }

    const rows = useMemo(() => campaignsWithNumber?.map((row) => ([
        row['csp_campaign.name'],
        row.did,
        renderOptionsColumn(row)
    ])) || [], [deleteSmsNumberFeature, campaignsWithNumber])

    const onDeleteHandler = async () => {
        if (deleting == null || isLoading) return
        setNumberNetworkError(null)
        deleteReachedNetworkPending.current = false
        setIsLoading(true)
        try {
            const deleteParms = await getDeleteSmsNumberParams(deleting)
            await deleteNumber(deleteParms)
        } catch {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (isLoading && networkPending) {
            deleteReachedNetworkPending.current = true
            return
        }

        if (isLoading && !networkPending && (deleteReachedNetworkPending.current || numberNetworkError)) {
            deleteReachedNetworkPending.current = false
            setDeleting(null)
            setIsLoading(false)
        }
    }, [isLoading, networkPending, numberNetworkError])

    const error = useMemo(() => {
        if (numberNetworkError == null) return null
        const response = numberNetworkError?.data || numberNetworkError
        if (response?.error || response?.message === 'failure') {
            return getValue('sms_enrollment_number_campaign_error')
        }
        return numberNetworkError
    }, [numberNetworkError])

    if (!campaignsWithNumber.length) {
        return <LandingPage onClickAddNew={onClickAddNew} />
    }

    return <ListContainer
        onClickAddNew={onClickAddNew}
        addButtonText={getValue('sms_enrollment_add_new_number')}
    >
        {error && (
            <Alert
                level='error'
                onClose={() => setNumberNetworkError(null)}
                className={classes.listErrorAlert}
                data-testid='sms-tabs-list-error-alert'
            >
                {error}
            </Alert>
        )}
        <Table
            columns={[
                getValue('sms_enrollment_campaign_id_column'),
                getValue('sms_enrollment_sms_number_column_name'),
                ''
            ]}
            rows={rows}
            data-testid='sms-tabs-list-table'
        />
        <ConfirmDeleteModal
            isOpen={deleting != null}
            isLoading={isLoading}
            type={'number'}
            selectedItem={deleting}
            onClose={() => {
                deleteReachedNetworkPending.current = false
                setDeleting(null)
                setIsLoading(false)
            }}
            onDelete={onDeleteHandler}
        />
    </ListContainer>
}

export default List
